import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { WIDGET_REGISTRY } from "@/dashboard/widgets";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings2, ArrowUp, ArrowDown, Eye, EyeOff, RotateCcw, Save, X, Plus, Sparkles } from "lucide-react";
import HomeTodayStrip from "@/components/HomeTodayStrip";
import HomeQuickActions from "@/components/HomeQuickActions";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

export default function Dashboard() {
  const { user, production } = useAuth();
  const [state, setState] = useState(null);
  const [editing, setEditing] = useState(false);
  const [layout, setLayout] = useState([]);

  useEffect(() => {
    if (!production?.id) return;
    api.get("/dashboard/state").then((r) => {
      setState(r.data);
      setLayout(r.data.layout);
    }).catch(() => setState({ error: true }));
  }, [production?.id]);

  if (!state) return <div className="text-zinc-400">Loading dashboard…</div>;
  if (state.error) return <div className="text-red-400">Could not load dashboard.</div>;

  const catalog = state.catalog || [];
  const activeIds = editing ? layout : state.layout;

  const move = (id, dir) => {
    const arr = [...layout];
    const i = arr.indexOf(id);
    if (i < 0) return;
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setLayout(arr);
  };

  const remove = (id) => setLayout((l) => l.filter((x) => x !== id));
  const add = (id) => setLayout((l) => (l.includes(id) ? l : [...l, id]));
  const resetToRoleDefault = () => setLayout(state.default_layout_for_role || []);

  const save = async () => {
    try {
      const { data } = await api.patch("/dashboard/layout", { widgets: layout });
      setState((s) => ({ ...s, layout: data.widgets }));
      setLayout(data.widgets);
      setEditing(false);
      toast.success("Dashboard saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const cancel = () => { setLayout(state.layout); setEditing(false); };

  const available = catalog.filter((c) => !layout.includes(c.id));

  return (
    <div className="space-y-6 animate-fade-up" data-testid="dashboard-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Production</div>
          <h1 className="mt-1 font-display text-4xl md:text-5xl tracking-tight">
            {state.production?.title || "Dashboard"}
          </h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.24em] text-gold inline-flex items-center gap-1.5" data-testid="dashboard-role-label">
              <Sparkles className="w-3 h-3" /> {state.production_role || "Company member"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <button data-testid="dash-cancel" onClick={cancel}
                className="h-9 px-3 rounded border border-white/10 text-sm text-zinc-300 hover:bg-white/5 inline-flex items-center gap-1.5">
                <X className="w-4 h-4" /> Cancel
              </button>
              <button onClick={resetToRoleDefault}
                className="h-9 px-3 rounded border border-white/10 text-sm text-zinc-300 hover:bg-white/5 inline-flex items-center gap-1.5">
                <RotateCcw className="w-4 h-4" /> Reset to role default
              </button>
              <button data-testid="dash-save" onClick={save} style={goldBtn}
                className="h-9 px-4 rounded font-semibold text-sm inline-flex items-center gap-1.5">
                <Save className="w-4 h-4" /> Save
              </button>
            </>
          ) : (
            <button data-testid="dash-customize" onClick={() => setEditing(true)}
              className="h-9 px-3 rounded border border-gold/40 text-gold hover:bg-gold/10 text-sm inline-flex items-center gap-1.5">
              <Settings2 className="w-4 h-4" /> Customize
            </button>
          )}
        </div>
      </header>

      {/* Sprint-audit iter 22: Today strip + Quick actions */}
      <HomeTodayStrip role={user.role} />
      <HomeQuickActions role={user.role} />

      {/* Widget grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
        {activeIds.map((id) => {
          const entry = WIDGET_REGISTRY[id];
          if (!entry) return null;
          const W = entry.component;
          return (
            <div key={id} className={`relative ${entry.span === 2 ? "md:col-span-2 xl:col-span-2" : ""}`}>
              {editing && (
                <div className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-black/70 backdrop-blur rounded border border-white/10 p-0.5">
                  <button data-testid={`w-up-${id}`} onClick={() => move(id, "up")} className="p-1.5 hover:bg-white/5 rounded text-zinc-300" title="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button data-testid={`w-down-${id}`} onClick={() => move(id, "down")} className="p-1.5 hover:bg-white/5 rounded text-zinc-300" title="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
                  <button data-testid={`w-remove-${id}`} onClick={() => remove(id)} className="p-1.5 hover:bg-red-500/10 rounded text-red-400" title="Remove"><EyeOff className="w-3.5 h-3.5" /></button>
                </div>
              )}
              <W data={state.data || {}} />
            </div>
          );
        })}
      </div>

      {editing && available.length > 0 && (
        <div className="rounded-2xl border border-dashed border-white/15 bg-[#0A0A0A] p-5">
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-3 inline-flex items-center gap-1.5">
            <Eye className="w-3 h-3" /> Add widgets
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {available.map((w) => (
              <button
                key={w.id}
                data-testid={`w-add-${w.id}`}
                onClick={() => add(w.id)}
                className="text-left rounded-lg border border-white/10 bg-[#121212] p-3 hover:border-gold/40 hover:bg-gold/5 transition-colors group"
              >
                <div className="inline-flex items-center gap-1.5 text-sm text-zinc-100">
                  <Plus className="w-3.5 h-3.5 text-gold" /> {w.title}
                </div>
                <div className="mt-1 text-[11px] text-zinc-500">{w.description}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
